import React from 'react';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';
import { CONTACT_EMAIL } from '../config';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="glass-card p-5 space-y-2">
    <p className="text-white/90 font-semibold">{title}</p>
    <div className="text-white/55 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const TermsOfService: React.FC = () => {
  useSEO(
    'Terms of Service — Purgr',
    'Terms of Service for Purgr, the free Instagram follow checker. Read about acceptable use, liability limitations, and your rights.'
  );

  return (
    <PageLayout title="Terms of Service">
      <div className="space-y-4">
        <p className="text-white/50 text-xs mb-6">Last updated: March 2025</p>

        <Section title="Agreement to terms">
          <p>
            By using Purgr ("the Service"), you agree to these Terms of Service. If you don't agree, please don't use the Service.
          </p>
        </Section>

        <Section title="1. Description of service">
          <p>
            Purgr is a free, browser-based tool that helps you compare your Instagram following and followers lists to identify accounts that don't follow you back. The Service processes files locally in your browser and does not require an Instagram login.
          </p>
        </Section>

        <Section title="2. Acceptable use">
          <p>You agree to use Purgr only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Use the Service in any way that violates Instagram's or Meta's Terms of Service.</li>
            <li>Attempt to interfere with or disrupt the Service or its infrastructure.</li>
            <li>Use automated scripts, bots, or scraping tools against the Service.</li>
            <li>Attempt to reverse-engineer, decompile, or extract source code from the Service for commercial use without permission.</li>
            <li>Use the Service to harass, stalk, or harm other Instagram users.</li>
          </ul>
        </Section>

        <Section title="3. No affiliation with Instagram">
          <p>
            Purgr is an independent tool and is not affiliated with, endorsed by, or sponsored by Instagram or Meta Platforms, Inc. "Instagram" is a trademark of Meta Platforms, Inc.
          </p>
        </Section>

        <Section title="4. Intellectual property">
          <p>
            The Purgr name, logo, and original content on this site are owned by Purgr and may not be copied or used without permission. The underlying code is proprietary.
          </p>
          <p>
            Your Instagram data files remain yours. Purgr claims no rights over any data you upload.
          </p>
        </Section>

        <Section title="5. No warranty (as-is)">
          <p>
            The Service is provided "as is" and "as available" without any warranties of any kind, express or implied. We make no guarantees that the Service will be accurate, error-free, uninterrupted, or that it will meet your expectations.
          </p>
          <p>
            Instagram may change their data export format at any time, which could temporarily affect Purgr's ability to parse files correctly.
          </p>
        </Section>

        <Section title="6. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Purgr and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to any decisions you make about who to follow or unfollow on Instagram.
          </p>
        </Section>

        <Section title="7. Third-party links">
          <p>
            Purgr contains links to third-party websites (Ko-fi, Gumroad, Instagram growth tools). These links are provided for convenience. We are not responsible for the content, privacy practices, or terms of any third-party sites.
          </p>
        </Section>

        <Section title="8. Changes to terms">
          <p>
            We reserve the right to update these Terms at any time. We'll update the date at the top of this page. Continued use of the Service after changes constitutes acceptance of the new Terms.
          </p>
        </Section>

        <Section title="9. Governing law">
          <p>
            These Terms are governed by the laws of the State of California, United States, without regard to conflict of law principles.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Questions about these Terms? Reach out at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-white/60 hover:text-white/90 underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </Section>
      </div>
    </PageLayout>
  );
};

export default TermsOfService;
